// Keeps the map-selected ticket card just below the pinned venue map after the
// caller's initial smooth scrollIntoView.
//
// Two rules decide what happens after that first scroll:
// - Layout change is the only trigger. A ResizeObserver watches the card, its
//   list container and the map; when late layout lands (ticket rows/images
//   rendering after ticketsLoading flips) and leaves the card behind the map's
//   live bottom edge, the card is re-aligned once per shift. Nothing runs per
//   frame, so the settle is quiet when nothing moves.
// - The member's own scroll ends it for good. The map is position: sticky, so
//   scrolling down into the ticket list moves the card up behind the map —
//   exactly the geometry a correction reacts to. A loop that kept correcting
//   would yank the member back to the card whenever they paused, so the first
//   wheel / touch / key / pointer input tears down every listener and the
//   observer. A manual scroll is never overridden.
// A short backstop (SETTLE_BACKSTOP_MS) ends observance even with no input, so
// an unrelated later resize cannot scroll the page.

// Breathing room between the pinned map's bottom edge and the selected card.
export const MAP_SCROLL_GAP_PX = 16;

// How long after the selection a layout shift may still be corrected. Covers
// the ticket rows/images settling after the fetch; past it, a resize is no
// longer attributable to the selection.
export const SETTLE_BACKSTOP_MS = 2500;

// The card must sit more than this many px behind the map to warrant a jump.
const OVERLAP_TOLERANCE_PX = 1;

// Inputs that mean the member is steering the scroll themselves. pointerdown
// covers scrollbar drags; touchstart / wheel / keydown the rest.
export const USER_SCROLL_EVENTS = ['wheel', 'touchstart', 'keydown', 'pointerdown'] as const;

export interface SettleEnv {
  /**
   * Calls onResize when any target's size changes AFTER observation starts
   * (the initial notification a ResizeObserver delivers on observe is not a
   * layout change). Returns a disconnect function.
   */
  observeResize(targets: readonly Element[], onResize: () => void): () => void;
  /**
   * Calls onUserScroll on the member's first scroll-steering input anywhere in
   * the page. Returns a function that removes every listener.
   */
  listenForUserScroll(card: HTMLElement, onUserScroll: () => void): () => void;
  /** One-shot timer ending observance; returns a cancel function. */
  startBackstop(onExpire: () => void, ms: number): () => void;
  /** False in engines that ignore scroll-margin; corrections then use scrollBy. */
  scrollMarginSupported: boolean;
}

export const observeResize: SettleEnv['observeResize'] = (targets, onResize) => {
  if (typeof ResizeObserver === 'undefined') {
    return () => undefined;
  }
  const seen = new Set<Element>();
  const observer = new ResizeObserver((entries) => {
    let changed = false;
    for (const { target } of entries) {
      if (seen.has(target)) {
        changed = true;
      } else {
        seen.add(target);
      }
    }
    if (changed) {
      onResize();
    }
  });
  for (const target of targets) {
    observer.observe(target);
  }
  return () => observer.disconnect();
};

// Where the member's input events can be heard. Inside the embed the card
// lives in a shadow root; its host's document still receives every composed
// input event (from inside the widget and from the host page around it), so
// listen there. In the SPA that is simply the card's document.
export const getUserScrollTarget = (card: Element): EventTarget => {
  const root = card.getRootNode();
  if ('host' in root) {
    const { host } = root as ShadowRoot;
    return host.ownerDocument ?? host;
  }
  return card.ownerDocument ?? root;
};

export const listenForUserScroll: SettleEnv['listenForUserScroll'] = (card, onUserScroll) => {
  const target = getUserScrollTarget(card);
  // Capture so a handler that stops propagation cannot hide the input.
  const options: AddEventListenerOptions = { capture: true, passive: true };
  for (const type of USER_SCROLL_EVENTS) {
    target.addEventListener(type, onUserScroll, options);
  }
  return () => {
    for (const type of USER_SCROLL_EVENTS) {
      target.removeEventListener(type, onUserScroll, options);
    }
  };
};

const defaultEnv = (): SettleEnv => ({
  observeResize,
  listenForUserScroll,
  startBackstop: (onExpire, ms) => {
    const id = setTimeout(onExpire, ms);
    return () => clearTimeout(id);
  },
  scrollMarginSupported: typeof CSS !== 'undefined' && CSS.supports('scroll-margin-top', '1px'),
});

// Parent in the flat tree: the parentElement, or — at a shadow-root boundary,
// where parentElement is null — the shadow host.
const nextAncestor = (node: Element): Element | null => {
  if (node.parentElement) {
    return node.parentElement;
  }
  const root = node.getRootNode();
  return 'host' in root ? (root as ShadowRoot).host : null;
};

// Nearest ancestor (or window) that actually scrolls for `element`, crossing
// shadow-root boundaries so the embed's scroller outside the shadow host is
// found. Only used by the scroll-margin-unsupported fallback.
export const getScrollParent = (
  element: Element,
  getStyle: (node: Element) => Pick<CSSStyleDeclaration, 'overflowY'> = (node) =>
    getComputedStyle(node)
): Window | Element => {
  let node = nextAncestor(element);
  while (node) {
    const { overflowY } = getStyle(node);
    if (/(auto|scroll|overlay)/.test(overflowY) && node.scrollHeight > node.clientHeight) {
      return node;
    }
    node = nextAncestor(node);
  }
  return window;
};

// Starts observance after the caller's initial scroll. On each observed layout
// change (until the member scrolls, the backstop fires, or the returned
// cleanup runs): refresh scroll-margin-top from the map's current height, and
// if the card's top now sits more than 1px above the map's bottom (+ gap),
// re-apply scrollIntoView({ block: 'start' }) — idempotent, targets the right
// scroller even across the embed's shadow root, honours scroll-margin-top — or
// scrollBy(-overlap) on the scroll parent where scroll-margin is unsupported.
export const settleCardBelowStickyMap = (
  card: HTMLElement,
  stickyMap: HTMLElement,
  env?: Partial<SettleEnv>
): (() => void) => {
  const {
    observeResize: observe,
    listenForUserScroll: listen,
    startBackstop,
    scrollMarginSupported,
  } = { ...defaultEnv(), ...env };
  const applyScrollMargin = () => {
    card.style.scrollMarginTop = `${stickyMap.offsetHeight + MAP_SCROLL_GAP_PX}px`;
  };
  applyScrollMargin();

  let stopped = false;
  const teardowns: (() => void)[] = [];
  const stop = () => {
    if (stopped) {
      return;
    }
    stopped = true;
    for (const teardown of teardowns.splice(0)) {
      teardown();
    }
  };

  const onLayoutChange = () => {
    if (stopped) {
      return;
    }
    applyScrollMargin();
    const cardTop = card.getBoundingClientRect().top;
    const mapBottom = stickyMap.getBoundingClientRect().bottom;
    const overlap = mapBottom + MAP_SCROLL_GAP_PX - cardTop;
    if (overlap <= OVERLAP_TOLERANCE_PX) {
      return;
    }
    if (scrollMarginSupported) {
      card.scrollIntoView({ block: 'start', behavior: 'auto' });
      return;
    }
    // The card is too HIGH on screen (behind the map): scroll back up by
    // exactly the overlap.
    getScrollParent(card).scrollBy({ top: -overlap });
  };

  // The card's list container grows when sibling rows render above the card,
  // which moves the card without resizing it — observe it too.
  const targets = [card, card.parentElement, stickyMap].filter(
    (target, index, all): target is HTMLElement => target !== null && all.indexOf(target) === index
  );
  teardowns.push(
    listen(card, stop),
    observe(targets, onLayoutChange),
    startBackstop(stop, SETTLE_BACKSTOP_MS)
  );
  // A re-selection or unmount mid-settle must not leave an observer nudging
  // the scroll from stale measurements.
  return stop;
};
